import * as React from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { Spin, Alert, Button } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { onAuthStateChange, isAuthorized, isShopAuthorized, signOutUser, checkEmailLink, signInWithEmail, getStoredEmail } from './firebase/auth';
import type { User } from './firebase/auth';
import { Login } from './components/Login';
import { Admin } from './components/Admin';
import { ShopLogin } from './components/ShopLogin';
import { ShopView } from './components/ShopView';
import { getDocument } from './firebase/firestore';

// Component to handle email link authentication before routing
function EmailLinkHandler({ children }: { children: React.ReactElement }): React.ReactElement {
    const [processing, setProcessing] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        async function handleEmailLink() {
            // Check if this is an email link callback
            if (checkEmailLink()) {
                try {
                    const storedEmail = getStoredEmail();
                    if (storedEmail) {
                        await signInWithEmail(storedEmail);
                        // Auth state change will handle the rest
                    } else {
                        setError('Email not found. Please request a new sign-in link.');
                    }
                } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to complete sign-in');
                    console.error('Email link sign in error:', err);
                }
            }
            setProcessing(false);
        }

        handleEmailLink();
    }, []);

    if (processing) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Completing sign-in..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
                <Alert
                    message="Sign-in Error"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return children;
}

// Admin route component
function AdminRoute(): React.ReactElement {
    const [user, setUser] = React.useState<User | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [authorized, setAuthorized] = React.useState<boolean>(false);
    const [signingOut, setSigningOut] = React.useState<boolean>(false);

    React.useEffect(() => {
        const unsubscribe = onAuthStateChange((currentUser) => {
            setUser(currentUser);
            setAuthorized(isAuthorized(currentUser));
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading..." />
            </div>
        );
    }

    if (!user) {
        return <Login />;
    }

    const handleSignOut = async () => {
        try {
            setSigningOut(true);
            await signOutUser();
        } catch (err) {
            console.error('Error signing out:', err);
        } finally {
            setSigningOut(false);
        }
    };

    if (!authorized) {
        return (
            <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
                <Alert
                    message="Access Denied"
                    description="You are not authorized to access this admin panel. Please contact the administrator."
                    type="error"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
                <Button
                    icon={<LogoutOutlined />}
                    onClick={handleSignOut}
                    loading={signingOut}
                    block
                >
                    Sign Out
                </Button>
            </div>
        );
    }

    return <Admin />;
}

// Shop route component
function ShopRoute(): React.ReactElement {
    const { shopUrl } = useParams<{ shopUrl: string }>();
    const [user, setUser] = React.useState<User | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [authorized, setAuthorized] = React.useState<boolean>(false);
    const [signingOut, setSigningOut] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!shopUrl) {
            setLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChange(async (currentUser) => {
            setUser(currentUser);
            
            if (currentUser) {
                // Fetch shop to check adminEmails
                try {
                    const shopData = await getDocument('shops', shopUrl);
                    if (shopData?.adminEmails && Array.isArray(shopData.adminEmails)) {
                        setAuthorized(isShopAuthorized(currentUser, shopData.adminEmails));
                    } else {
                        setAuthorized(false);
                    }
                } catch (error) {
                    console.error('Error fetching shop:', error);
                    setAuthorized(false);
                }
            } else {
                setAuthorized(false);
            }
            
            setLoading(false);
        });

        return () => unsubscribe();
    }, [shopUrl]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading..." />
            </div>
        );
    }

    if (!shopUrl) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert message="Invalid URL" description="Shop URL is missing." type="error" showIcon />
            </div>
        );
    }

    if (!user) {
        return <ShopLogin shopUrl={shopUrl} onAuthSuccess={() => {}} />;
    }

    const handleSignOut = async () => {
        try {
            setSigningOut(true);
            await signOutUser();
        } catch (err) {
            console.error('Error signing out:', err);
        } finally {
            setSigningOut(false);
        }
    };

    if (!authorized) {
        return (
            <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
                <Alert
                    message="Access Denied"
                    description="You are not authorized to access this shop. Please contact the shop administrator."
                    type="error"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
                <Button
                    icon={<LogoutOutlined />}
                    onClick={handleSignOut}
                    loading={signingOut}
                    block
                >
                    Sign Out
                </Button>
            </div>
        );
    }

    return <ShopView shopUrl={shopUrl} />;
}

export function App(props: {}): React.ReactElement {
    return (
        <BrowserRouter>
            <EmailLinkHandler>
                <Routes>
                    <Route path="/admin" element={<AdminRoute />} />
                    <Route path="/shop/:shopUrl" element={<ShopRoute />} />
                    <Route path="/" element={<Navigate to="/admin" replace />} />
                </Routes>
            </EmailLinkHandler>
        </BrowserRouter>
    );
}
